import { Card, CardBody, CardHeader, CardTitle } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { useRelatedBusinesses, type BusinessParent } from '@/api/businesses-admin';
import { statusTone } from '@/pages/account/components/status-tone';
import { InfoGrid } from '@/pages/account/components/InfoGrid';
import { TabLoading } from '@/pages/customers/detail/tab-states';
import { formatWeekdayDateTime } from '@/lib/format';

const cap = (s?: string | null) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ') : '—';

/** Related-trips card: companion list fetched from /:id/related. */
export function BusinessRelatedCard({ businessId }: { businessId: number }) {
  const { data: related, isLoading } = useRelatedBusinesses(businessId);

  if (!isLoading && (!related || related.length === 0)) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Related trips</CardTitle>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <TabLoading rows={3} />
        ) : (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {related!.map((r) => (
              <div
                key={r.business_id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gap: 'var(--vn-space-3)',
                  alignItems: 'center',
                  padding: 'var(--vn-space-2) var(--vn-space-3)',
                  background: 'var(--vn-bg-subtle)',
                  borderRadius: 'var(--vn-radius-sm)',
                  fontSize: 'var(--vn-font-size-sm)',
                }}
              >
                <span
                  style={{
                    color: 'var(--vn-primary-text)',
                    fontWeight: 'var(--vn-font-weight-medium)',
                  }}
                >
                  IBN-{r.business_id}
                </span>
                <span>
                  {r.employee_name}
                  <span
                    style={{ color: 'var(--vn-fg-muted)', marginLeft: 'var(--vn-space-2)' }}
                  >
                    ({r.department_name})
                  </span>
                </span>
                <StatusPill tone={statusTone(r.business_status)}>
                  {cap(r.business_status)}
                </StatusPill>
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/** Parent-trip info card (rendered when this trip is a companion). */
export function BusinessParentCard({ parent }: { parent: BusinessParent }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Parent trip</CardTitle>
      </CardHeader>
      <CardBody>
        <InfoGrid
          items={[
            { label: 'Code', value: `IBN-${parent.business_id}` },
            { label: 'Employee', value: parent.employee_name },
            { label: 'Department', value: parent.department_name },
            {
              label: 'Status',
              value: (
                <StatusPill tone={statusTone(parent.business_status)}>
                  {cap(parent.business_status)}
                </StatusPill>
              ),
            },
            { label: 'Location', value: parent.business_place },
            { label: 'Start', value: formatWeekdayDateTime(parent.business_startdate) },
          ]}
        />
      </CardBody>
    </Card>
  );
}
